import { Internal } from '@gleamer/types';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { WithStatus } from '../../../atoms/WithStatus';
import { useFileReport } from './useFileReport';

type EnhancedDicomJSONReport = Internal.EnhancedDicomJSONReport;
type LabelTask = Internal.LabelTask;

export type ReportDisplayProps = {
  report: EnhancedDicomJSONReport;
  fetchReport: (report: EnhancedDicomJSONReport) => Promise<Blob>;
  embedded?: boolean;
  className?: string;
  task?: LabelTask;
};

export const removeRedundantSpaces = (text: string): string => {
  return text.replace(/(\r\n|\n|\r){3,}/gm, '\n\n').trim();
};

type ReportHighlight = { regexes: string[]; color?: string };

/** The task's highlight patterns as one case-insensitive expression; a pattern that does not compile is left out. */
function highlightPattern(highlights: ReportHighlight[] = []): RegExp | null {
  const sources = highlights
    .flatMap(highlight => highlight.regexes ?? [])
    .filter(source => {
      try {
        new RegExp(source);
        return source.length > 0;
      } catch {
        return false;
      }
    });
  return sources.length ? new RegExp(`(${sources.join('|')})`, 'gi') : null;
}

/**
 * The report's text cut into plain and highlighted pieces. Rendered as React text, never as HTML: a report is data
 * from a hospital, and its text must not become markup in the page.
 */
export function highlightSegments(
  text: string,
  highlights: ReportHighlight[] = []
): { text: string; highlighted: boolean }[] {
  const pattern = highlightPattern(highlights);
  if (!pattern) {
    return [{ text, highlighted: false }];
  }
  const segments: { text: string; highlighted: boolean }[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    if (!match[0]) {
      continue;
    }
    const at = match.index ?? 0;
    if (at > last) {
      segments.push({ text: text.slice(last, at), highlighted: false });
    }
    segments.push({ text: match[0], highlighted: true });
    last = at + match[0].length;
  }
  if (last < text.length) {
    segments.push({ text: text.slice(last), highlighted: false });
  }
  return segments;
}

/** The distinct highlighted words a report mentions, lower-cased, in the order they first appear. */
export function reportMentions(text: string, highlights: ReportHighlight[] = []): string[] {
  const seen = new Set<string>();
  highlightSegments(text, highlights)
    .filter(segment => segment.highlighted)
    .forEach(segment => seen.add(segment.text.toLowerCase()));
  return Array.from(seen);
}

function ReportTextDisplay({
  report,
  fetchReport,
  className,
  embedded,
  task,
}: ReportDisplayProps) {
  const { data, isError, isLoading } = useQuery({
    queryKey: ['report', report.url],
    queryFn: async () => {
      const blob = await fetchReport(report);
      const text = await blob.text();
      return removeRedundantSpaces(text);
    },
    // This caches data for the whole duration of the session
    staleTime: Infinity,
  });

  return (
    <WithStatus className={className} loading={isLoading} error={isError}>
      {data && (
        <pre
          className="max-w-3xl whitespace-pre-wrap break-words font-sans text-dl-text"
          data-testid={`report-${report.id}`}
        >
          {embedded && '\n\n\n'}
          {highlightSegments(data, task?.reportHighlights).map((segment, index) =>
            segment.highlighted ? (
              // the design's amber: a keyword the task asks to notice, not an annotation colour
              <mark
                key={index}
                className="rounded-[4px] bg-dl-warning-tint px-0.5 font-semibold text-dl-warning"
              >
                {segment.text}
              </mark>
            ) : (
              <React.Fragment key={index}>{segment.text}</React.Fragment>
            )
          )}
        </pre>
      )}
    </WithStatus>
  );
}

function ReportFileDisplay({
  report,
  fetchReport,
  className,
}: ReportDisplayProps) {
  const {
    data: url,
    isLoading,
    isError,
  } = useFileReport({
    report,
    fetchReport,
    enabled: true,
  });

  return (
    <WithStatus
      data-testid={`report-${report.id}`}
      loading={isLoading}
      error={isError}
      className={className}
    >
      {url && (
        <iframe
          title={report.id}
          src={url}
          role="document"
          className="h-full w-full"
        />
      )}
    </WithStatus>
  );
}

export function ReportDisplay({
  report,
  fetchReport,
  className,
  embedded,
  task,
}: ReportDisplayProps) {
  if (report.contentType === 'text/plain') {
    return (
      <ReportTextDisplay
        className={className}
        report={report}
        fetchReport={fetchReport}
        embedded={embedded}
        task={task}
      />
    );
  }
  return (
    <ReportFileDisplay
      className={className}
      report={report}
      fetchReport={fetchReport}
      embedded={embedded}
    />
  );
}
